// EU VAT rates (verified for 2026). Each country has standard, reduced1, reduced2 (or null).
// reduced1 / reduced2 represent the most commonly applied reduced rates.
// Categories map: standard | reduced | digital | food | books | medicine
//
// Mapping notes (general EU practice — not country-specific overrides):
//  - digital  -> standard rate (digital services taxed at standard in nearly all EU countries since 2015)
//  - food     -> reduced rate (typically reduced1)
//  - books    -> reduced rate (typically reduced2 if exists else reduced1)
//  - medicine -> reduced rate (typically reduced1)
// Where a reduced rate doesn't exist, fall back to standard.

export const EU_COUNTRIES = [
    { code: "AT", name: "Austria", slug: "austria", flag: "🇦🇹", standard: 20.0, reduced1: 10.0, reduced2: 13.0 },
    { code: "BE", name: "Belgium", slug: "belgium", flag: "🇧🇪", standard: 21.0, reduced1: 6.0, reduced2: 12.0 },
    { code: "BG", name: "Bulgaria", slug: "bulgaria", flag: "🇧🇬", standard: 20.0, reduced1: 9.0, reduced2: null },
    { code: "HR", name: "Croatia", slug: "croatia", flag: "🇭🇷", standard: 25.0, reduced1: 5.0, reduced2: 13.0 },
    { code: "CY", name: "Cyprus", slug: "cyprus", flag: "🇨🇾", standard: 19.0, reduced1: 5.0, reduced2: 9.0 },
    { code: "CZ", name: "Czech Republic", slug: "czech-republic", flag: "🇨🇿", standard: 21.0, reduced1: 12.0, reduced2: null },
    { code: "DK", name: "Denmark", slug: "denmark", flag: "🇩🇰", standard: 25.0, reduced1: null, reduced2: null },
    { code: "EE", name: "Estonia", slug: "estonia", flag: "🇪🇪", standard: 24.0, reduced1: 9.0, reduced2: null },
    { code: "FI", name: "Finland", slug: "finland", flag: "🇫🇮", standard: 25.5, reduced1: 10.0, reduced2: 13.5 },
    { code: "FR", name: "France", slug: "france", flag: "🇫🇷", standard: 20.0, reduced1: 5.5, reduced2: 10.0 },
    { code: "DE", name: "Germany", slug: "germany", flag: "🇩🇪", standard: 19.0, reduced1: 7.0, reduced2: null },
    { code: "GR", name: "Greece", slug: "greece", flag: "🇬🇷", standard: 24.0, reduced1: 6.0, reduced2: 13.0 },
    { code: "HU", name: "Hungary", slug: "hungary", flag: "🇭🇺", standard: 27.0, reduced1: 5.0, reduced2: 18.0 },
    { code: "IE", name: "Ireland", slug: "ireland", flag: "🇮🇪", standard: 23.0, reduced1: 9.0, reduced2: 13.5 },
    { code: "IT", name: "Italy", slug: "italy", flag: "🇮🇹", standard: 22.0, reduced1: 5.0, reduced2: 10.0 },
    { code: "LV", name: "Latvia", slug: "latvia", flag: "🇱🇻", standard: 21.0, reduced1: 5.0, reduced2: 12.0 },
    { code: "LT", name: "Lithuania", slug: "lithuania", flag: "🇱🇹", standard: 21.0, reduced1: 5.0, reduced2: 12.0 },
    { code: "LU", name: "Luxembourg", slug: "luxembourg", flag: "🇱🇺", standard: 17.0, reduced1: 3.0, reduced2: 8.0 },
    { code: "MT", name: "Malta", slug: "malta", flag: "🇲🇹", standard: 18.0, reduced1: 5.0, reduced2: 7.0 },
    { code: "NL", name: "Netherlands", slug: "netherlands", flag: "🇳🇱", standard: 21.0, reduced1: 9.0, reduced2: null },
    { code: "PL", name: "Poland", slug: "poland", flag: "🇵🇱", standard: 23.0, reduced1: 5.0, reduced2: 8.0 },
    { code: "PT", name: "Portugal", slug: "portugal", flag: "🇵🇹", standard: 23.0, reduced1: 6.0, reduced2: 13.0 },
    { code: "RO", name: "Romania", slug: "romania", flag: "🇷🇴", standard: 21.0, reduced1: 9.0, reduced2: null },
    { code: "SK", name: "Slovakia", slug: "slovakia", flag: "🇸🇰", standard: 23.0, reduced1: 5.0, reduced2: 19.0 },
    { code: "SI", name: "Slovenia", slug: "slovenia", flag: "🇸🇮", standard: 22.0, reduced1: 5.0, reduced2: 9.5 },
    { code: "ES", name: "Spain", slug: "spain", flag: "🇪🇸", standard: 21.0, reduced1: 4.0, reduced2: 10.0 },
    { code: "SE", name: "Sweden", slug: "sweden", flag: "🇸🇪", standard: 25.0, reduced1: 6.0, reduced2: 12.0 },
];

// Category IDs are stable; user-facing labels & descriptions are localized in i18n/translations.js.
export const PRODUCT_CATEGORY_IDS = ["standard", "reduced", "digital", "food", "books", "medicine"];

/**
 * Resolve the applicable VAT rate for a given country and product category.
 * Returns { rate: number, label: string }.
 */
export function getApplicableRate(country, category) {
    if (!country) return { rate: 0, label: "—" };
    const r1 = country.reduced1;
    const r2 = country.reduced2;

    switch (category) {
        case "standard":
        case "digital":
            return { rate: country.standard, label: "Standard" };
        case "reduced": {
            const rate = r1 ?? country.standard;
            return { rate, label: r1 != null ? "Reduced" : "Standard (no reduced)" };
        }
        case "food":
        case "medicine": {
            const rate = r1 ?? country.standard;
            return { rate, label: r1 != null ? "Reduced" : "Standard (no reduced)" };
        }
        case "books": {
            const rate = r2 ?? r1 ?? country.standard;
            return { rate, label: r2 != null || r1 != null ? "Reduced" : "Standard (no reduced)" };
        }
        default:
            return { rate: country.standard, label: "Standard" };
    }
}

export function findCountry(code) {
    return EU_COUNTRIES.find((c) => c.code === code);
}

export function findCountryBySlug(slug) {
    if (!slug) return null;
    return EU_COUNTRIES.find((c) => c.slug === slug.toLowerCase());
}

export function parsePairSlug(pair) {
    // e.g. "germany-to-france" -> { from: <country>, to: <country> }
    if (!pair || typeof pair !== "string") return null;
    const m = pair.toLowerCase().split("-to-");
    if (m.length !== 2) return null;
    const from = findCountryBySlug(m[0]);
    const to = findCountryBySlug(m[1]);
    if (!from || !to) return null;
    return { from, to };
}

export const OSS_THRESHOLD_EUR = 10000;
